import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Coins, Plus, Loader2, Users, Eye, Trophy, Megaphone, Gift } from "lucide-react";
import { toast } from "sonner";

const COST = 20;

export default function ProSponsored() {
  const { lang } = useT();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [producer, setProducer] = useState(null);
  const [games, setGames] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    topic: "",
    questions: 10,
    days_visible: 30,
    sponsor_link: "",
    game_type: "quiz",
    reward: { kind: "none", code: "", value: "", description: "", redeem_url: "", google_review_url: "" },
  });
  const [busy, setBusy] = useState(false);
  const [leadsModalGameId, setLeadsModalGameId] = useState(null);
  const [leads, setLeads] = useState([]);

  const refresh = async () => {
    try {
      const [b, p, g] = await Promise.all([
        api.get("/me/billing"),
        api.get("/producers-mine/me"),
        api.get("/games/sponsored-mine/list"),
      ]);
      setBilling(b.data);
      setProducer(p.data);
      setGames(g.data);
    } catch (e) {
      toast.error(lang === "fr" ? "Chargement impossible" : "Loading failed");
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!producer) {
      toast.error(lang === "fr" ? "Créez d'abord votre fiche producteur" : "Create your producer profile first");
      navigate("/pro/producer");
      return;
    }
    if ((billing?.credits ?? 0) < COST) {
      toast.error(lang === "fr" ? `Vous avez ${billing?.credits ?? 0}/${COST} crédits` : `You have ${billing?.credits ?? 0}/${COST} credits`);
      return;
    }
    if (!form.title.trim() || !form.topic.trim()) {
      toast.error(lang === "fr" ? "Titre et sujet requis" : "Title and topic required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        reward: form.reward?.kind && form.reward.kind !== "none" ? form.reward : null,
      };
      await api.post("/games/sponsored", payload);
      toast.success(lang === "fr" ? "Jeu sponsorisé publié" : "Sponsored game published");
      setShowForm(false);
      setForm({ title: "", topic: "", questions: 10, days_visible: 30, sponsor_link: "", game_type: "quiz", reward: { kind: "none", code: "", value: "", description: "", redeem_url: "", google_review_url: "" } });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Publication échouée" : "Publish failed"));
    } finally {
      setBusy(false);
    }
  };

  const openLeads = async (gameId) => {
    setLeadsModalGameId(gameId);
    try {
      const { data } = await api.get(`/games/sponsored-mine/${gameId}/leads`);
      setLeads(data);
    } catch {
      toast.error(lang === "fr" ? "Chargement des leads impossible" : "Could not load leads");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <span className="chip-pink">{lang === "fr" ? "Espace Pro" : "Pro space"}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
            {lang === "fr" ? "Jeux sponsorisés" : "Sponsored games"}
          </h1>
          <p className="text-[#4A5568] mt-3">
            {lang === "fr"
              ? `Publiez un quiz, une chasse au trésor ou un memory autour de votre domaine. Coût : ${COST} crédits par jeu. Les joueurs qui dépassent 70 % et qui consentent deviennent vos leads qualifiés et peuvent débloquer la récompense que vous configurez.`
              : `Publish a quiz, treasure hunt or memory game around your estate. Cost: ${COST} credits per game. Players above 70% who opt-in become qualified leads and unlock the reward you configure.`}
          </p>
        </div>
        <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-4 text-right">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1]">{lang === "fr" ? "Crédits" : "Credits"}</div>
          <div className="font-display text-2xl text-[#E5276A] inline-flex items-center gap-1.5"><Coins className="w-5 h-5" />{billing?.credits ?? "…"}</div>
        </div>
      </div>

      <div className="mt-8">
        {!producer && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            {lang === "fr"
              ? <>Pour publier, créez d'abord votre fiche dans <button onClick={() => navigate("/pro/producer")} className="underline font-bold">Espace Pro &gt; Mon producteur</button>.</>
              : <>To publish, first create your profile in <button onClick={() => navigate("/pro/producer")} className="underline font-bold">Pro &gt; My producer</button>.</>}
          </div>
        )}
        <button
          data-testid="sponsored-new"
          onClick={() => setShowForm((s) => !s)}
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]"
        >
          <Plus className="w-4 h-4" /> {showForm ? (lang === "fr" ? "Annuler" : "Cancel") : (lang === "fr" ? "Nouveau quiz" : "New quiz")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-5 rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 space-y-4" data-testid="sponsored-form">
          {/* Game type selector */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Type de jeu" : "Game type"}</label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="sponsored-game-type-group">
              {[
                { key: "quiz", label_fr: "Quiz QCM", label_en: "MCQ Quiz" },
                { key: "treasure_hunt", label_fr: "Chasse au trésor", label_en: "Treasure hunt" },
                { key: "memory", label_fr: "Memory / paires", label_en: "Memory / pairs" },
              ].map((g) => (
                <button
                  type="button"
                  key={g.key}
                  data-testid={`sponsored-game-type-${g.key}`}
                  onClick={() => setForm({ ...form, game_type: g.key })}
                  className={`px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${
                    form.game_type === g.key
                      ? "border-[#E5276A] bg-[#FCE4ED] text-[#E5276A]"
                      : "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                  }`}
                >
                  {lang === "fr" ? g.label_fr : g.label_en}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Titre du jeu *" : "Game title *"}</label>
              <input data-testid="sponsored-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={lang === "fr" ? "Découvrez notre Pinot Noir" : "Discover our Pinot Noir"} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Sujet (briefing IA) *" : "Topic (AI brief) *"}</label>
              <input data-testid="sponsored-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder={lang === "fr" ? "Bourgogne, Pinot Noir, Premier Cru" : "Burgundy, Pinot Noir, Premier Cru"} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{form.game_type === "memory" ? (lang === "fr" ? "Paires" : "Pairs") : (lang === "fr" ? "Étapes / questions" : "Questions / steps")}</label>
              <input type="number" min={4} max={15} value={form.questions} onChange={(e) => setForm({ ...form, questions: parseInt(e.target.value) || 10 })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Visible (jours)" : "Visible (days)"}</label>
              <input type="number" min={7} max={365} value={form.days_visible} onChange={(e) => setForm({ ...form, days_visible: parseInt(e.target.value) || 30 })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Lien sponsor (optionnel)" : "Sponsor link (optional)"}</label>
              <input data-testid="sponsored-link" value={form.sponsor_link} onChange={(e) => setForm({ ...form, sponsor_link: e.target.value })} placeholder="https://" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
          </div>

          {/* Conversion / Reward block */}
          <div className="rounded-xl border border-dashed border-[#E5276A]/40 bg-[#FFF8FB] p-4 space-y-3" data-testid="sponsored-reward-block">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-[#E5276A]" />
              <h3 className="font-display text-base text-[#1F3A5F]">{lang === "fr" ? "Récompense / conversion" : "Reward / conversion"}</h3>
            </div>
            <p className="text-[11px] text-[#4A5568]">{lang === "fr" ? "Débloquée si le joueur atteint 70 %." : "Unlocked when player scores ≥ 70%."}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="sponsored-reward-kind-group">
              {[
                { key: "none", label_fr: "Aucune", label_en: "None" },
                { key: "voucher", label_fr: "Code promo", label_en: "Voucher" },
                { key: "discount", label_fr: "Remise %", label_en: "Discount %" },
                { key: "tasting", label_fr: "Dégustation", label_en: "Tasting" },
                { key: "google_review", label_fr: "Avis Google", label_en: "Google Review" },
              ].map((k) => (
                <button
                  type="button"
                  key={k.key}
                  data-testid={`sponsored-reward-kind-${k.key}`}
                  onClick={() => setForm({ ...form, reward: { ...form.reward, kind: k.key } })}
                  className={`px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                    form.reward?.kind === k.key
                      ? "border-[#E5276A] bg-[#FCE4ED] text-[#E5276A]"
                      : "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                  }`}
                >
                  {lang === "fr" ? k.label_fr : k.label_en}
                </button>
              ))}
            </div>
            {form.reward?.kind && form.reward.kind !== "none" && (
              <div className="grid sm:grid-cols-2 gap-2">
                {(form.reward.kind === "voucher" || form.reward.kind === "discount") && (
                  <input data-testid="sponsored-reward-code" value={form.reward.code} onChange={(e) => setForm({ ...form, reward: { ...form.reward, code: e.target.value } })} placeholder={form.reward.kind === "voucher" ? (lang === "fr" ? "Code (ex: GEOVINUM10)" : "Code (e.g. GEOVINUM10)") : (lang === "fr" ? "10 % p. ex." : "e.g. 10%")} className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
                )}
                <input data-testid="sponsored-reward-value" value={form.reward.value} onChange={(e) => setForm({ ...form, reward: { ...form.reward, value: e.target.value } })} placeholder={lang === "fr" ? "Valeur (ex: -10 € la bouteille)" : "Value (e.g. -€10 per bottle)"} className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
                {form.reward.kind === "google_review" && (
                  <input data-testid="sponsored-reward-review-url" value={form.reward.google_review_url} onChange={(e) => setForm({ ...form, reward: { ...form.reward, google_review_url: e.target.value } })} placeholder="https://g.page/r/..." className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
                )}
                {(form.reward.kind === "voucher" || form.reward.kind === "discount" || form.reward.kind === "tasting") && (
                  <input data-testid="sponsored-reward-redeem-url" value={form.reward.redeem_url} onChange={(e) => setForm({ ...form, reward: { ...form.reward, redeem_url: e.target.value } })} placeholder={lang === "fr" ? "URL pour réclamer (boutique, RDV...)" : "Redeem URL (shop, booking…)"} className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
                )}
                <input data-testid="sponsored-reward-desc" value={form.reward.description} onChange={(e) => setForm({ ...form, reward: { ...form.reward, description: e.target.value } })} placeholder={lang === "fr" ? "Phrase d'incentive (1 ligne)" : "Incentive (1 line)"} className="sm:col-span-2 px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#1F3A5F]/10">
            <span className="text-xs text-[#4A5568]">{lang === "fr" ? `Coût : ${COST} crédits seront déduits.` : `Cost: ${COST} credits will be deducted.`}</span>
            <button disabled={busy} data-testid="sponsored-submit" type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              {lang === "fr" ? "Publier" : "Publish"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-10">
        <h2 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Vos quiz publiés" : "Your published quizzes"}</h2>
        {games.length === 0 ? (
          <p className="text-sm text-[#8B95A1] italic mt-3" data-testid="sponsored-empty">{lang === "fr" ? "Aucun quiz publié pour l'instant." : "No quiz published yet."}</p>
        ) : (
          <ul className="mt-4 grid md:grid-cols-2 gap-4" data-testid="sponsored-list">
            {games.map((g) => (
              <li key={g.id} className="rounded-xl border border-[#1F3A5F]/12 bg-white p-5">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-bold text-[#1F3A5F]">{g.title}</h3>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">
                    {g.game_type === "treasure_hunt" ? `${(g.steps || []).length} ${lang === "fr" ? "étapes" : "steps"}` :
                     g.game_type === "memory" ? `${(g.pairs || []).length} ${lang === "fr" ? "paires" : "pairs"}` :
                     `${(g.questions || []).length} Q`}
                  </span>
                </div>
                <p className="text-xs text-[#4A5568] mt-1">{g.topic}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A]">
                    {g.game_type === "treasure_hunt" ? (lang === "fr" ? "Chasse" : "Hunt") :
                     g.game_type === "memory" ? "Memory" :
                     "Quiz"}
                  </span>
                  {g.reward?.kind && g.reward.kind !== "none" && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                      <Gift className="w-2.5 h-2.5" />
                      {g.reward.kind === "voucher" ? (lang === "fr" ? "Code promo" : "Voucher") :
                       g.reward.kind === "discount" ? (lang === "fr" ? "Remise" : "Discount") :
                       g.reward.kind === "tasting" ? (lang === "fr" ? "Dégustation" : "Tasting") :
                       g.reward.kind === "google_review" ? (lang === "fr" ? "Avis Google" : "Google review") : "Reward"}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-[#4A5568]">
                  <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-[#E5276A]" /> {g.plays || 0}</span>
                  <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-[#E5276A]" /> {g.leads_count || 0} {lang === "fr" ? "leads" : "leads"}</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button data-testid={`sponsored-leads-${g.id.slice(0,8)}`} onClick={() => openLeads(g.id)} className="px-3 py-1 rounded-full text-xs font-bold border border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F]">
                    {lang === "fr" ? "Voir leads" : "View leads"}
                  </button>
                  <a href={`/games/sponsored/${g.id}`} className="px-3 py-1 rounded-full text-xs font-bold border border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F]">
                    {lang === "fr" ? "Prévisualiser" : "Preview"}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {leadsModalGameId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end md:items-center justify-center md:p-5" onClick={() => setLeadsModalGameId(null)}>
          <div className="bg-white w-full max-w-xl max-h-[92dvh] md:max-h-[85dvh] overflow-y-auto p-6 rounded-t-2xl md:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between sticky top-0 bg-white -mx-6 px-6 -mt-6 pt-6 pb-3 border-b border-[#F0EAE0]">
              <h3 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Leads qualifiés" : "Qualified leads"}</h3>
              <button onClick={() => setLeadsModalGameId(null)} aria-label="Fermer" className="w-9 h-9 rounded-full bg-[#FBF7F3] hover:bg-[#FCE4ED] text-[#1F3A5F] hover:text-[#E5276A] flex items-center justify-center">✕</button>
            </div>
            <p className="text-xs text-[#4A5568] mt-1">{lang === "fr" ? "Score > 70 % avec consentement marketing" : "Score > 70% with marketing opt-in"}</p>
            <ul className="mt-4 space-y-2" data-testid="sponsored-leads-list">
              {leads.length === 0 && <li className="text-sm text-[#8B95A1] italic">{lang === "fr" ? "Aucun lead pour l'instant." : "No leads yet."}</li>}
              {leads.map((l) => (
                <li key={l.id} className="rounded-lg border border-[#1F3A5F]/12 p-3 text-sm">
                  <div className="font-bold text-[#1F3A5F]">{l.name || l.email}</div>
                  <div className="text-xs text-[#4A5568]">{l.email} · <Users className="inline w-3 h-3" /> {Math.round((l.ratio || 0) * 100)}%</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
